import { useCallback, useState } from "react";

import { useApolloClient } from "@apollo/client";

import { TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useCheckAlreadyCompletedToast } from "flows/Inventory/shared/hooks/useCheckAlreadyCompletedToast";
import { useChecksRedirect } from "flows/Inventory/shared/hooks/useChecksRedirect";
import { useStartNextCheck } from "flows/Inventory/shared/hooks/useStartNextCheck";
import { useTaskCompletedToast } from "flows/Inventory/shared/hooks/useTaskCompletedToast";
import { getPriorityGroup } from "flows/Inventory/shared/models/check/serializer";
import { ErrorTypeForAnalytics, useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { reportErrorsToDDAndThrow } from "utils/datadog";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

import {
  ValidateFreshnessCheckDocument,
  ValidateFreshnessCheckMutation,
  ValidateFreshnessCheckMutationVariables,
} from "../queries/freshnessCheck/freshnessCheck.generated";

export type FreshCheckMutationContext = {
  checkId: string | null;
  origin: PageName;
  priority: number;
  shelfNumber: string | null;
  shelfLetter: string | null;
  sku: string | null;
};

export type DeclareStockParams = {
  quantityDamaged: number;
  quantityExpired: number;
  quantityTooGoodToGo: number;
  expectedQuantity: number;
};

export type UseFreshCheckMutationResult = {
  declareStock: (params: DeclareStockParams) => Promise<void>;
  isUpdating: boolean;
};

export function useFreshCheckMutation({
  context,
}: {
  context: FreshCheckMutationContext;
}): UseFreshCheckMutationResult {
  const client = useApolloClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const { showCheckAlreadyCompletedToast } = useCheckAlreadyCompletedToast();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "fresh_check_error",
  });
  const showTaskCompletedToast = useTaskCompletedToast();
  const { navigateToCheck, navigateToOrigin } = useChecksRedirect();
  const { startNextCheck } = useStartNextCheck();
  const { sendSegmentTrackEvent } = useAnalytics();

  const declareStock = useCallback(
    async (params: DeclareStockParams) => {
      const { quantityDamaged, quantityExpired, quantityTooGoodToGo, expectedQuantity } = params;

      if (isNullOrUndefined(context.checkId)) {
        throw new Error("No checkId present in context");
      }

      setIsUpdating(true);
      try {
        await reportErrorsToDDAndThrow(
          "updating fresh check",
          async () => {
            try {
              await client.mutate<
                ValidateFreshnessCheckMutation,
                ValidateFreshnessCheckMutationVariables
              >({
                mutation: ValidateFreshnessCheckDocument,
                variables: {
                  input: {
                    checkId: context.checkId!,
                    quantityDamaged,
                    quantityExpired,
                    quantityTooGoodToGo,
                  },
                },
              });
            } catch (e: any) {
              if (e?.graphQLErrors?.[0]?.extensions?.code === "TASK_UPDATE_CONFLICT") {
                throw new Error("taskAlreadyCompleted");
              }
              throw e;
            }
          },
          {
            context: {
              checkId: context.checkId,
              sku: context.sku,
              origin: context.origin,
              priority: context.priority,
              shelfNumber: context.shelfNumber,
              shelfLetter: context.shelfLetter,
            },
          },
        );

        // Track successful completion
        sendSegmentTrackEvent("stockCheckFinished", {
          type: "automated",
          check_id: context.checkId,
          product_sku: context.sku,
          quantity_expected: expectedQuantity,
          quantity_counted: 0,
          quantity_damaged: quantityDamaged,
          quantity_expired: quantityExpired,
          quantity_tgtg: quantityTooGoodToGo,
        });

        // Try to fetch next check
        try {
          const result = await startNextCheck({
            groupedPriority: getPriorityGroup(context.priority),
            shelfNumber: `${context.shelfNumber}${context.shelfLetter}`,
            type: [TaskType.freshness_check],
          });

          if (result.status === "noMoreCheck") {
            showTaskCompletedToast();
            navigateToOrigin(context.origin);
          } else if (result.status === "unknownError") {
            navigateToOrigin(context.origin);
          } else if (result.status === "checkAlreadyTaken") {
            navigateToOrigin(context.origin);
          } else if (isNotNullNorUndefined(result.check)) {
            navigateToCheck(result.check, context.origin);
          }
        } catch (error) {
          navigateToOrigin(context.origin);
        }
      } catch (e: any) {
        // Handle task already completed error
        if ((e?.message ?? e?.toString() ?? "").includes("taskAlreadyCompleted")) {
          showCheckAlreadyCompletedToast();
          navigateToOrigin(context.origin);
          return;
        }

        // Show generic error toast and track error
        showToastAndPlayError();
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.INVENTORY_FRESH_CHECK_LIST_PAGE,
          component_value: ErrorTypeForAnalytics.UNKNOWN,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      context,
      client,
      sendSegmentTrackEvent,
      showTaskCompletedToast,
      navigateToOrigin,
      startNextCheck,
      navigateToCheck,
      showCheckAlreadyCompletedToast,
      showToastAndPlayError,
    ],
  );

  return {
    declareStock,
    isUpdating,
  };
}
